import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'
import { kUserFilesFoldersApiBasePath } from '../constants'
import { RootFolderResponse, RootFolderResponseSchema } from '../schemas'

/**
 * Get the root folder for a matter.
 *
 * Every matter has exactly one root folder that serves as the top-level container.
 *
 * @param matterId - The matter ID (UUID)
 * @returns RootFolderResponse containing the root folder
 * @throws Error when user is not authenticated
 * @throws ApiError when the root-folder request fails
 */
export async function getRootFolder(matterId: string): Promise<RootFolderResponse> {
  const sentryTransactionId = nanoid()

  try {
    const auth = getAuth()
    const token = await auth.currentUser?.getIdToken()

    if (!token) {
      throw new Error('User not authenticated')
    }

    const url = `${kPaxtonAppApiRESTBaseUrl()}${kUserFilesFoldersApiBasePath}/root?matter_id=${encodeURIComponent(matterId)}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Transaction-Id': sentryTransactionId,
      },
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatUserFilesErrorDetail(errorData)

      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
          extra: {
            ...getResponseDebugContext(response, url),
            matterId,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })

      throw new ApiError(response.status, detail || `Failed to fetch root folder: ${response.status}`)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Root folder response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            matterId,
            parseError: formatUnknownError(parseError),
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid root folder response JSON')
    }

    const parsed = RootFolderResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Root folder response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            matterId,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid root folder response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: { matterId },
        })
      })
    }
    throw error
  }
}
